<template>
  <div
    class="content-grid-raw__item rounded-2 min-h-[220px] max-w-[256px] w-[256px] box-border bg-base cursor-pointer"
    v-on="{
      click: (e: MouseEvent) => onClick(e, item),
    }"
  >
    <ContentGridDeletedFileItem v-if="isEditMode && item.file?.is_deleted" @click="emit('deletedFileClicked')" />
    <ContentGridFileItem
      v-else-if="item.type === 'file'"
      :item="item"
      :show-preview-thumbnails="showPreviewThumbnails"
    />
    <div v-else-if="item.type === 'slide'" class="p-2 content-grid-raw__item__slide flex flex-col items-center">
      <div class="relative flex">
        <CImage
          :ref="(el) => (slideImgRefs[index] = el)"
          class="content-grid-raw__item__img rounded-2"
          height="135"
          :img-props="{
            draggable: false,
          }"
          :lazy="isDefaultDisplayMode"
          object-fit="cover"
          preview-disabled
          :src="item.slide.url"
          width="240"
        />
      </div>
      <div class="font-700 text-truncate w-full mt-2">
        {{ item.file.name }}
      </div>
      <div
        class="content-grid-raw__item__hint flex items-center justify-between text-center text-truncate w-full min-h-8"
      >
        {{ t('canvasUI.canvasBuilder.contentGrid.pageNumberDescription', { pageNumber: item.slide.index + 1 }) }}
        <CInfoBadge
          v-if="!isShareable"
          v-tooltip="t('canvasUI.components.ContentGrid.sharingDisabled')"
          :background-color="themeVars.error2"
          :color="themeVars.error"
          fa-type="fa-kit"
          icon="solid-share-from-square-slash"
          variation="minimal"
        />
      </div>
      <div v-if="canDownload" class="pt-1 flex justify-end w-full">
        <DownloadButton v-tooltip="t('canvasUI.components.ContentGrid.downloadSlide')" @click="downloadFile(item)" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref, inject, type ComputedRef } from 'vue'
import { omit } from 'lodash-es'

import CImage from '@lib/components/CImage/CImage.vue'
import {
  type FileOpenParams,
  type ContentGridItemFileProps,
  type ContentGridItemSlideProps,
  type ContentGridProps,
  CanvasBuilderMode,
} from '@canvas-builder/types/canvas'
import tooltipDirective from '@lib/directives/tooltip'
import useThemeVars from '@lib/composables/useThemeVars'
import useCanvas from '@canvas-builder/composables/useCanvas'
import DownloadButton from '@canvas-builder/components/container/DownloadButton/DownloadButton.vue'
import CInfoBadge from '@lib/components/CInfoBadge/CInfoBadge.vue'
import ContentGridFileItem from './ContentGridFileItem.vue'
import ContentGridDeletedFileItem from './ContentGridDeletedFileItem.vue'
import { isModifierClick } from '@lib/util/navigation.util'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { CanvasRetrieve } from '@openapi'

const props = defineProps<{
  index: number
  item: ContentGridItemSlideProps | ContentGridItemFileProps
  allItems: (ContentGridItemSlideProps | ContentGridItemFileProps)[]
  showPreviewThumbnails?: boolean
  usedInSectionId?: CanvasRetrieve['id']
}>()

const emit = defineEmits<(e: 'deletedFileClicked') => void>()
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const { t } = useI18n()
const themeVars = useThemeVars()
const vTooltip = tooltipDirective
const { onFileOpen, isEditMode, isDefaultDisplayMode, mode, onDownloadFile, canvasMetadata } = useCanvas()
const slideImgRefs = ref<any[]>([])

const isShareable = computed(() => {
  return props.item.file?.permissions?.can_share !== false
})

const canDownload = computed(() => {
  return (
    mode.value === CanvasBuilderMode.SHARING &&
    isDefaultDisplayMode.value &&
    props.item.file?.permissions?.can_download !== false &&
    canvasMetadata.value?.allow_download !== false
  )
})

function onFileClick(e: MouseEvent, item: ContentGridProps['items'][number]) {
  if (!item?.file) return

  const payload = {
    newTab: isModifierClick(e),
    source_file_id: item.file.id,
    page_index: 'slide' in item ? item?.slide?.index : undefined,
    ...omit(item.file, 'id'),
  } as FileOpenParams

  if (props.usedInSectionId) {
    payload.used_in_section_id = props.usedInSectionId
  }

  if (
    item.file.content_type === 'ar' &&
    launchDarkly.value.batch_open_content_grid_ar_files &&
    ['simultaneous', 'swap'].includes(launchDarkly.value.batch_open_content_grid_ar_files) &&
    props.allItems
  ) {
    payload.files = {
      ids: props.allItems.filter((i) => i.file.content_type === 'ar').map((i) => i.file.id),
      strategy: launchDarkly.value.batch_open_content_grid_ar_files as 'simultaneous' | 'swap',
    }
  }

  // always add file_id as a fallback
  payload.file_id = item.file.snapshot_id ?? item.file.id

  onFileOpen.value?.(payload)
}

function onClick(e: MouseEvent, item: ContentGridProps['items'][number]) {
  e.stopPropagation()

  if (e.button !== 0 || item.file?.is_deleted) {
    return
  } else if (item.type === 'file' || item.type === 'slide') {
    onFileClick(e, item)
  }
}

function downloadFile(item: ContentGridItemSlideProps | ContentGridItemFileProps) {
  onDownloadFile.value?.(item.file)
}
</script>

<style lang="scss" scoped>
.content-grid-raw__item {
  color: v-bind('themeVars.text');
  border: 1px solid v-bind('themeVars.primary5');
}

.content-grid-raw__item__img {
  outline: 1px solid v-bind('themeVars.primary6');
}

.content-grid-raw__item__hint {
  color: v-bind('themeVars.text3');
}
</style>
